import {
  ApiRoutes,
  CODIGO_UNIDADE_ATENDIMENTO,
  Mensagens,
  MotivoContato,
  TipoPessoa,
  TIPOS_LOCAL_OBSTRUCAO_ESGOTO,
  TIPOS_PESSOA,
} from "../../models/types";
import {
  getBaseURL,
  getDescricaoAdicionalProtocolo,
  getMatriculaResponsavelProtocolo,
  getMatriculaSolicitante,
  getOnlyNumbers,
  isCnpjValido,
  isCpfValido,
} from "../../utils/functions";
import React, { useState } from "react";
import { z } from "zod";
import { useIonLoading } from "@ionic/react";
import CardReCaptcha, {
  CardReCaptchaRef,
} from "../../components/form/CardReCaptcha";
import Page from "../../components/Page";
import useNotification from "../../hooks/useNotification";
import ReactForm from "../../components/form/ReactForm";
import InputText from "../../components/form/InputText";
import SubmitButton from "../../components/form/SubmitButton";
import Accordion from "../../components/Accordion";
import PesquisaCidade from "../../components/form/PesquisaCidade";
import PesquisaBairro from "../../components/form/PesquisaBairro";
import PesquisaLogradouro from "../../components/form/PesquisaLogradouro";
import InputComplete from "../../components/form/InputComplete";
import ButtonBack from "../../components/ButtonBack";
import DivDesobstrucaoEsgoto from "../../components/DivDesobstrucaoEsgoto";
import axios from "axios";
import SelectField from "../../components/form/SelectField";

export default function DesobstrucaoEsgotoNoEndereco() {
  const [present, dismiss] = useIonLoading();
  const [cidade, setCidade] = useState<number | null>();
  const [bairro, setBairro] = useState<number | null>();
  const [tipoCliente, setTipoCliente] = useState<string>();

  const notification = useNotification();
  const reCaptchaRef = React.createRef<CardReCaptchaRef>();

  const isTipoClientePessoaFisica = (() => {
    return tipoCliente === TipoPessoa.PESSOA_FISICA;
  })();

  const formSchema = z
    .object({
      codigoCidade: z
        .string()
        .min(1, "Selecione uma cidade.")
        .transform((val) => Number(getOnlyNumbers(val))),
      codigoBairro: z
        .string()
        .min(1, "Selecione um bairro.")
        .transform((val) => Number(getOnlyNumbers(val))),
      codigoLogradouro: z
        .string()
        .min(1, "Selecione um logradouro.")
        .transform((val) => Number(getOnlyNumbers(val))),
      quadraImovel: z.string().min(1, "Informe a quadra."),
      loteImovel: z.string().min(1, "Informe o lote."),
      numeroImovel: z.string().min(1, "Informe o número."),
      pontoReferencia: z.string().min(1, "Informe um ponto de referência."),
      obstrucao: z.string().min(1, "Selecione a local da obstrução."),
      observacao: z.string(),
      esgotoRetornando: z.string().min(1, "Selecione uma opção."),
      nomeContato: z.string().min(1, "Informe o nome do interessado."),
      cpfCnpj: z.string(),
      tipoCliente: z.any().transform((input) => input?.value),
      celular: z
        .string()
        .min(11, "Informe um número de contato.")
        .transform((val) => Number(getOnlyNumbers(val))),
      recaptcha: z.string().min(1, Mensagens.RECAPTCHA_VAZIO),
    })
    .refine(
      (data) =>
        data.cpfCnpj
          ? data.tipoCliente === TipoPessoa.PESSOA_FISICA
            ? isCpfValido(data.cpfCnpj)
            : isCnpjValido(data.cpfCnpj)
          : true,
      (data) => ({
        message:
          data.tipoCliente === TipoPessoa.PESSOA_FISICA
            ? "CPF inválido."
            : "CNPJ inválido.",
        path: ["cpfCnpj"],
      })
    );

  type formType = z.infer<typeof formSchema>;

  async function enviarDados(dados: formType, resetFields: any) {
    const _codigoCidade = dados?.codigoCidade || cidade;
    const _codigoBairro = dados?.codigoBairro || bairro;

    if (!dados || !_codigoCidade || !_codigoBairro || !dados.codigoLogradouro || dados.codigoLogradouro > 99999) {
      notification.showAlert({
        message: `Não conseguimos validar os dados da cidade, do bairro e do logradouro. Por favor selecione os novamente.`,
      });

      return;
    }

    await present({ message: Mensagens.SALVANDO });

    const dddTelefone = String(dados.celular).substring(0, 2);
    const numeroTelefone = String(dados.celular).substring(2, 11);

    try {
      let body = {
        dadosProtocolo: {
          dddTelefoneContato: Number(dddTelefone),
          numeroTelefoneContato: Number(numeroTelefone),
          codigoMotivoContato: MotivoContato.VAZAMENTO_DE_ESGOTO,
          matriculaResponsavel: getMatriculaResponsavelProtocolo(),
          descricaoAdicional: getDescricaoAdicionalProtocolo(),
          nomeContato: dados.nomeContato,
          numeroCpfCnpj: dados.cpfCnpj ? getOnlyNumbers(dados.cpfCnpj) : null,
          tipoCliente: dados.cpfCnpj ? dados.tipoCliente : "",
        },
        matriculaSolicitante: getMatriculaSolicitante(),
        codigoUnidadeAtendimento: CODIGO_UNIDADE_ATENDIMENTO,
        nomeContato: dados.nomeContato,
        tipoCliente: dados.cpfCnpj ? dados.tipoCliente : "",
        cpfCnpj: dados.cpfCnpj ? getOnlyNumbers(dados.cpfCnpj) : null,
        codigoCidade: _codigoCidade,
        codigoBairro:  _codigoBairro,
        codigoLogradouro: dados.codigoLogradouro,
        quadra: dados.quadraImovel,
        lote: dados.loteImovel,
        numero: dados.numeroImovel,
        pontoReferencia: dados.pontoReferencia,
        dddTelefoneContato: dddTelefone,
        numeroTelefoneContato: numeroTelefone,
        descricaoLocalObstrucao: dados.obstrucao,
        esgotoRetornando: dados.esgotoRetornando === "true" ? true : false,
        isEnderecoConta: false,
        observacao: dados.observacao,
      };

      let url =
        getBaseURL(ApiRoutes.ECO_ATENDIMENTO_EXTERNO) +
        "/atendimentos/registrar/esgoto-entupido";

      const params = new URLSearchParams({
        "g-recaptcha-response": dados.recaptcha,
      });

      const resp = await axios.post(url, JSON.stringify(body), {
        params: params,
        headers: {
          "content-type": "application/json",
        },
      });

      notification.showProtocolo(resp?.data).then(() => {
        resetFields();
      });

      dismiss();
    } catch (e: any) {
      dismiss();

      let msg = "";

      if (e.response.status === 401) {
        msg = Mensagens.RECAPTCHA_ERRO;
      }

      if (e.response.status === 403) {
        msg = Mensagens.SOLICITACAO_ABERTA;
      }

      await notification.showError({ message: msg, exception: e });
    } finally {
      reCaptchaRef?.current?.clear();
    }
  }

  return (
    <Page
      title="Desobstrução de esgoto"
      buttonType="back"
      showCardConta={false}
    >
      <div className="flex flex-col space-y-4">
        <Accordion title="Observações" open>
          <DivDesobstrucaoEsgoto />
        </Accordion>

        <div className="card bg-opacity-70">
          <div className="card-title">Dados da ocorrência</div>

          <div className="items-center">
            <ReactForm
              schema={formSchema}
              onError={(error, e) => {}}
              onSubmit={(data, resetFields) =>
                enviarDados(data as formType, resetFields)
              }
            >
              <div className="flex flex-col space-y-4">
                <PesquisaCidade
                  name="codigoCidade"
                  placeHolder="Cidade"
                  onSelect={(item) => {
                    setCidade(item?.value as number);
                  }}
                />

                <PesquisaBairro
                  name="codigoBairro"
                  placeHolder="Bairro"
                  codigoCidade={cidade}
                  onSelect={(item) => {
                    setBairro(item?.value as number);
                  }}
                />

                <PesquisaLogradouro
                  name="codigoLogradouro"
                  placeHolder="Logradouro"
                  codigoCidade={cidade}
                  codigoBairro={bairro}
                />

                <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-3 gap-4">
                  <InputText
                    name="quadraImovel"
                    label="Quadra"
                    inputProps={{ maxLength: 8 }}
                  />
                  <InputText
                    name="loteImovel"
                    label="Lote"
                    inputProps={{ maxLength: 9 }}
                  />
                  <InputText
                    name="numeroImovel"
                    label="Número"
                    inputProps={{ maxLength: 5 }}
                  />
                </div>

                <InputText
                  name="pontoReferencia"
                  label="Ponto de referência"
                  inputProps={{ maxLength: 50 }}
                />

                <InputText
                  name="nomeContato"
                  label="Nome do interessado"
                  inputProps={{ maxLength: 70 }}
                />

                <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-3 gap-4">
                  <SelectField
                    name="tipoCliente"
                    placeHolder="Tipo de pessoa"
                    defaultValuePosition={0}
                    onSelect={(item) => {
                      setTipoCliente(item?.value as string);
                    }}
                    items={TIPOS_PESSOA}
                  />

                  <InputText
                    name="cpfCnpj"
                    label={
                      isTipoClientePessoaFisica
                        ? "CPF do interessado"
                        : "CNPJ do interessado"
                    }
                    mask={
                      isTipoClientePessoaFisica
                        ? "999.999.999-99"
                        : "99.999.999/9999-99"
                    }
                  />

                  <InputText
                    name="celular"
                    label="Celular"
                    mask="99 99999-9999"
                  />
                </div>

                <InputComplete
                  name="obstrucao"
                  placeHolder="Local da obstrução"
                  showDivDetalhes
                  titleDivDetalhes="Descrição da localização"
                  items={TIPOS_LOCAL_OBSTRUCAO_ESGOTO}
                />

                <InputComplete
                  name="esgotoRetornando"
                  placeHolder="Esgoto está retornando para dentro do imóvel?"
                  items={[
                    { value: "true", label: "Sim" },
                    { value: "false", label: "Não" },
                  ]}
                />

                <InputText
                  name="observacao"
                  label="Observação"
                  inputProps={{ maxLength: 90 }}
                />

                <CardReCaptcha name="recaptcha" ref={reCaptchaRef} />

                <div className="grid grid-cols-2 gap-2 xs:grid-cols-1">
                  <SubmitButton label="Enviar" />
                  <ButtonBack label="Voltar" />
                </div>
              </div>
            </ReactForm>
          </div>
        </div>
      </div>
    </Page>
  );
}
